/**
 * 审批相关工具函数
 * 封装通用的审批通过和驳回逻辑
 */
import { handleApproval } from '@/utils/workflowDesigner'
import { getDate } from '@/api/Select'
import { Toast } from 'vant-green'

/**
 * 根据表名获取对应的列表页路径
 * @param {string} tableName - 表名
 * @returns {string} 列表页路径
 */
export function getListPathByTableName(tableName) {
  const tableNameMap = {
    'FIN_FEE': '/approval/expense/list',      // 报销单
    'PUR_ORD': '/approval/purchase/list',     // 采购订单
    'ORD': '/approval/sales/list',            // 销售订单
    'ORD_FORECAST': '/approval/forecast/list' // 预测单
  }
  
  return tableNameMap[tableName] || '/approval'
}

/**
 * 返回到对应的列表页面
 * @param {Object} component - Vue 组件实例
 * @param {string} tableName - 表名
 */
export function backToList(component, tableName) {
  const listPath = getListPathByTableName(tableName)
  component.$router.push(listPath)
}

/**
 * 处理审批通过操作
 * @param {Object} options - 配置选项
 * @param {Object} options.component - Vue 组件实例 (this)
 * @param {Object} options.v_cfmRow - 审批记录数据
 * @param {string} options.v_rmk - 审批备注
 * @param {string} options.v_username - 用户账号
 * @param {string} options.v_usernm - 用户名称
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 失败回调
 * @returns {Promise}
 */
export function handleApproveAction(options) {
  const {
    component,
    v_cfmRow,
    v_rmk = '',
    v_username,
    v_usernm,
    onSuccess,
    onError
  } = options

  return new Promise((resolve, reject) => {
    component.$dialog.confirm({
      title: '提示',
      message: '你确定要同意此记录吗?',
      confirmButtonText: '确认',
      cancelButtonText: '取消'
    }).then(() => {
      // 构建更新条件
      let v_cond = ''
      if (v_cfmRow['TABNM'] === 'RD_MAT') {
        v_cond = ` set ordstfg='Y' ,chkuser='${v_usernm}',chkdttm='${getDate('full')}'`
      } else {
        v_cond = ` set stfg='Y' ,chkuser='${v_usernm}',chkdttm='${getDate('full')}'`
      }

      // 调用审批流程
      handleApproval({
        businessId: v_cfmRow['IDSEQ'],
        approverId: v_username,
        approverName: v_usernm,
        action: 'approve',
        comment: v_rmk,
        component: component,
        DIRNM: v_cfmRow['IDTY'],
        CNAME: v_cfmRow['TITLE'],
        FRUSER: v_cfmRow['ADDUSER'],
        axios: component.$axios,
        EMPNO: v_username,
        tableName: v_cfmRow['TABNM'],
        UPD_COND: v_cond,
        CHKRMK: v_rmk,
      }).then((res) => {
        Toast({
          message: '当前审批完成',
          duration: 500,
        })

        // 执行成功回调
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(res)
        } else {
          // 默认行为：返回到对应的列表页面
          setTimeout(() => {
            backToList(component, v_cfmRow['TABNM'])
          }, 1000)
        }

        resolve(res)
      }).catch((error) => {
        const errorMsg = error.message || '请联系管理员'
        Toast({
          type: 'fail',
          message: '审批操作失败：' + errorMsg,
          duration: 1000,
        })

        // 执行失败回调
        if (onError && typeof onError === 'function') {
          onError(error)
        }

        reject(error)
      })
    }).catch((err) => {
      console.log('用户取消了操作')
      reject(new Error('用户取消操作'))
    })
  })
}

/**
 * 处理审批驳回操作
 * @param {Object} options - 配置选项
 * @param {Object} options.component - Vue 组件实例 (this)
 * @param {Object} options.v_cfmRow - 审批记录数据
 * @param {string} options.v_rmk - 驳回原因（必填）
 * @param {string} options.v_username - 用户账号
 * @param {string} options.v_usernm - 用户名称
 * @param {Function} options.onSuccess - 成功回调
 * @param {Function} options.onError - 失败回调
 * @returns {Promise}
 */
export function handleRejectAction(options) {
  const {
    component,
    v_cfmRow,
    v_rmk = '',
    v_username,
    v_usernm,
    onSuccess,
    onError
  } = options

  return new Promise((resolve, reject) => {
    // 验证驳回原因
    if (!v_rmk || v_rmk.trim() === '') {
      Toast({
        type: 'fail',
        message: '请填写驳回原因',
        duration: 1000,
      })
      reject(new Error('驳回原因不能为空'))
      return
    }

    component.$dialog.confirm({
      title: '提示',
      message: '你确定要驳回此记录吗?',
      confirmButtonText: '确认',
      cancelButtonText: '取消'
    }).then(() => {
      // 构建更新条件
      let v_cond = ''
      if (v_cfmRow['TABNM'] === 'RD_MAT') {
        v_cond = ` set ordstfg='N',REDORMK='${v_rmk}',appuser=null`
      } else {
        v_cond = ` set stfg='N',REDORMK='${v_rmk}',appuser=null`
      }

      // 执行驳回操作
      component.$axios({
        method: 'post',
        url: 'xuper_auth/updateRec',
        data: {
          p_key: 'idseq',
          p_value: v_cfmRow['IDSEQ'],
          p_table: v_cfmRow['TABNM'],
          p_cond: v_cond
        },
      }).then(res => {
        if (res.code === '200') {
          Toast({
            type: 'success',
            message: '驳回成功',
            duration: 500,
          })

          // 执行成功回调
          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess(res)
          } else {
            // 默认行为：返回到对应的列表页面
            setTimeout(() => {
              backToList(component, v_cfmRow['TABNM'])
            }, 1000)
          }

          resolve(res)
        } else {
          Toast({
            type: 'fail',
            message: res.result || '驳回失败',
            duration: 1000,
          })

          const error = new Error(res.result || '驳回失败')
          if (onError && typeof onError === 'function') {
            onError(error)
          }
          reject(error)
        }
      }).catch(err => {
        Toast({
          type: 'fail',
          message: '网络请求失败',
          duration: 1000,
        })

        if (onError && typeof onError === 'function') {
          onError(err)
        }
        reject(err)
      })
    }).catch((err) => {
      console.log('用户取消了操作')
      reject(new Error('用户取消操作'))
    })
  })
}

/**
 * 简化版：直接从组件实例获取所需参数
 * @param {Object} component - Vue 组件实例 (this)
 * @param {string} action - 操作类型：'approve' 或 'reject'
 * @param {Object} callbacks - 回调函数 { onSuccess, onError }
 * @returns {Promise}
 */
export function handleApprovalAction(component, action, callbacks = {}) {
  const options = {
    component,
    v_cfmRow: component.v_cfmRow,
    v_rmk: component.v_rmk,
    v_usernm: component.$cookies.get('v_usernm'),
    v_username: component.$cookies.get('v_telno'),
    v_corpid: component.$cookies.get('v_corpid'),
    onSuccess: callbacks.onSuccess,
    onError: callbacks.onError
  }

  if (action === 'approve') {
    return handleApproveAction(options)
  } else if (action === 'reject') {
    return handleRejectAction(options)
  } else {
    return Promise.reject(new Error('未知的操作类型'))
  }
}
